/**
 * OutreachFilters — the search box, Sort by menu and Filters button that sit
 * on the inbox title row (Figma order). Reuses RosterSearchSort's search box.
 */
import type { OutreachRowsParams } from '@discovr/features';
import type { OutreachInboxTab, OutreachTab as OutreachApiTab } from '@discovr/types';
import { RosterSearchSort } from '../parts/RosterSearchSort';
import { CalendarBlank, CaretDown } from '../../../design-system/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { INBOX_SORTS } from './inboxTabs';
import { SORT_MENU, SORT_MENU_GROUP, SORT_MENU_ITEM } from './menuStyles';
import { FilterPanel, type OutreachFilterState } from './FilterPanel';

type InboxSort = NonNullable<OutreachRowsParams['sort']>;

export interface OutreachFiltersProps {
  campaignId: string;
  /** Already mapped to the BE's tab value — the same one the rows query uses. */
  tab: OutreachApiTab;
  inboxTab: OutreachInboxTab;
  query: string;
  onQueryChange: (q: string) => void;
  sort: InboxSort;
  onSortChange: (sort: InboxSort) => void;
  filters: OutreachFilterState;
  onFiltersChange: (next: OutreachFilterState) => void;
}

export function OutreachFilters({ campaignId, tab, inboxTab, query, onQueryChange, sort, onSortChange, filters, onFiltersChange }: OutreachFiltersProps) {
  // Figma's Sort by menu only ever shows Last activity/Name A–Z/Stage —
  // "Scheduled time" only makes sense once there's something scheduled to
  // sort by, so it's added just for that one tab.
  const sortOptions = tab === 'scheduled' ? INBOX_SORTS : INBOX_SORTS.filter((option) => option.value !== 'scheduled_at');
  return (
    // Figma 505:49941: 12px between controls, a 273px search box (505:49942).
    <div data-testid="outreach-controls" className="flex flex-wrap items-center justify-end gap-3">
      <RosterSearchSort placeholder="Search creators" query={query} onQueryChange={onQueryChange} searchClassName="w-[273px]" inputClassName="rounded-xl px-3.5 pl-9" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Sort by: ${sortOptions.find((option) => option.value === sort)?.label ?? sort}`}
            className="flex h-10 shrink-0 items-center gap-2 rounded-xl border border-border bg-card px-4 text-body-sm-medium text-foreground transition-colors hover:bg-muted"
          >
            <CalendarBlank size={16} aria-hidden="true" /> Sort by <CaretDown size={16} aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={SORT_MENU}>
          <DropdownMenuRadioGroup value={sort} onValueChange={(value) => onSortChange(value as InboxSort)} className={SORT_MENU_GROUP}>
            {sortOptions.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value} className={SORT_MENU_ITEM}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <FilterPanel campaignId={campaignId} tab={tab} inboxTab={inboxTab} query={query} value={filters} onChange={onFiltersChange} />
    </div>
  );
}
